import React from 'react';
import { Card, CardContent, CardHeader, Typography, Box } from '@mui/material';
import {
    PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend
} from 'recharts';
import { translateLabel } from './data';


// Couleurs calquées sur votre maquette
const CHART_COLORS = ['#003366', '#c9a84c', '#4CAF50', '#F44336', '#9C27B0', '#00BCD4'];

export const formatNumber = (num) => new Intl.NumberFormat('fr-FR').format(num);

// ── Tooltip Personnalisé ─────────────────────────────────────────────────────
// On ajoute "total" dans les arguments
function CustomTooltip({ active, payload, total }) {
    if (!active || !payload?.length) return null;
    const item = payload[0];

    // On utilise le total passé par le parent, sinon on replie sur la valeur de l'item
    const percentage = total > 0 ? ((item.value / total) * 100).toFixed(1) : '0';

    return (
        <Box sx={{
            borderRadius: 2, px: 1.5, py: 1, boxShadow: 3,
            border: '1px solid rgba(201,168,76,0.2)',
            backgroundColor: '#1a1a2e', color: '#faf7f2'
        }}>
            <Typography sx={{ fontWeight: 500, color: '#c9a84c', fontSize: '0.75rem' }}>
                {item.name}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem' }}>
                {formatNumber(item.value)} ({percentage}%)
            </Typography>
        </Box>
    );
}
// ── Donut Chart (Format 50/50 avec légende personnalisée) ────────────────────
export default function DonutChartCard({ title, data, colors }) {
    // Préparation des données
    const chartData = data.map((d) => {
        const itemLabel = d?.label || d?.statut || 'Inconnu'; // Fallback sur statut
        return {
            name: translateLabel(itemLabel),
            value: parseInt(d?.count || 0, 10),
            label: itemLabel,
        };
    });
    console.log(chartData);

    const total = chartData.reduce((s, d) => s + d.value, 0);

    return (
        <Card elevation={0} sx={{ border: '1px solid #e0d8c8', backgroundColor: '#faf7f2', height: '100%' }}>
            <CardHeader
                title={
                    <Typography sx={{ color: '#1a1a2e', fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem', fontWeight: 600 }}>
                        {title}
                    </Typography>
                }
                sx={{ pb: 1 }}
            />
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

                    {/* Section Gauche : Le Graphique (w-1/2) */}
                    <Box sx={{ width: '50%', height: 192 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={chartData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={45}
                                    outerRadius={75}
                                    paddingAngle={3}
                                    dataKey="value"
                                    stroke="none"
                                >
                                    {chartData.map((_, index) => (
                                        <Cell key={index} fill={colors[index % colors.length]} />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip total={total} />} />
                            </PieChart>
                        </ResponsiveContainer>
                    </Box>

                    {/* Section Droite : La Légende Personnalisée (w-1/2) */}
                    <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {chartData.map((item, i) => {
                            const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : '0';
                            return (
                                <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {/* Pastille de couleur */}
                                    <Box
                                        sx={{
                                            width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                                            backgroundColor: colors[i % colors.length]
                                        }}
                                    />

                                    {/* Textes et Barre de progression */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Typography noWrap sx={{ fontSize: '0.75rem', color: '#1a1a2e' }}>
                                                {item.name}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, ml: 1, color: '#1a1a2e' }}>
                                                {formatNumber(item.value)}
                                            </Typography>
                                        </Box>

                                        {/* Background de la barre */}
                                        <Box sx={{ width: '100%', height: 4, borderRadius: 2, mt: 0.5, backgroundColor: '#f0ebe0' }}>
                                            {/* Fill de la barre */}
                                            <Box
                                                sx={{
                                                    height: 4, borderRadius: 2, transition: 'all 0.3s ease',
                                                    width: `${Math.min(parseFloat(pct), 100)}%`,
                                                    backgroundColor: colors[i % colors.length]
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                </Box>
            </CardContent>
        </Card>
    );
}

// ── 2. Horizontal Bar Chart (Catégories, Gestion) ────────────────────────────
export function HBarChartCard({ title, data, colors = CHART_COLORS }) {
    const chartData = data.map((d) => ({
        name: d.label,
        value: parseInt(d.count, 10),
    }));

    return (
        <Card elevation={0} sx={{ border: '1px solid #e0d8c8', backgroundColor: '#faf7f2', height: '100%' }}>
            <CardHeader
                title={<Typography variant="subtitle1" fontWeight="bold" color="#1a1a2e">{title}</Typography>}
                sx={{ pb: 0 }}
            />
            <CardContent>
                <Box sx={{ height: 280, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e0d8c8" />
                            <XAxis type="number" tick={{ fontSize: 12 }} />
                            <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12, fill: '#1a1a2e' }} />
                            <Tooltip formatter={(value) => [formatNumber(value), 'Volume']} />
                            <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={24}>
                                {chartData.map((_, index) => (
                                    <Cell key={index} fill={colors[index % colors.length]} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </CardContent>
        </Card>
    );
}

// ── 3. Vertical Bar Chart (Top Genres) ───────────────────────────────────────
export function VBarChartCard({ title, data, color = '#1a1a2e', maxItems = 10 }) {
    const sliced = data.slice(0, maxItems);
    const chartData = sliced.map((d) => ({
        name: d.label.length > 12 ? d.label.slice(0, 11) + '…' : d.label,
        fullName: d.label,
        value: parseInt(d.count, 10),
    }));

    return (
        <Card elevation={0} sx={{ border: '1px solid #e0d8c8', backgroundColor: '#faf7f2', height: '100%' }}>
            <CardHeader
                title={<Typography variant="subtitle1" fontWeight="bold" color="#1a1a2e">{title}</Typography>}
                sx={{ pb: 0 }}
            />
            <CardContent>
                <Box sx={{ height: 280, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ left: 0, right: 10, top: 20, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0d8c8" />
                            <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#1a1a2e' }} angle={-30} textAnchor="end" height={60} />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip
                                formatter={(value, _, props) => [formatNumber(value), props.payload.fullName]}
                            />
                            <Bar dataKey="value" fill={color} radius={[6, 6, 0, 0]} maxBarSize={45} />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </CardContent>
        </Card>
    );
}